import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  HeroSection,
  HeroImageContainer,
  HeroImage,
  Overlay,
  Container,
  TopRow,
  TitleColumn,
  TitleWrapper,
  Title,
  BottomRow,
  LeftInfoColumn,
  DateText,
  SquareMeters,
  RightInfoColumn,
  BuyButton,
  DownloadButton,
} from './Hero.styled';
import CatalogueForm from './CatalogueForm';

const Hero = () => {
  const [showDownloadForm, setShowDownloadForm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const heroImages = useMemo(
    () => ['/img/Hero1.jpeg', '/img/Hero2.jpeg', '/img/Hero3.jpeg'],
    []
  );

  // Preload all images
  useEffect(() => {
    const imagePromises = heroImages.map((src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = src;
      });
    });

    Promise.all(imagePromises)
      .then(() => setImagesLoaded(true))
      .catch((error) => console.error('Error loading images:', error));
  }, [heroImages]);

  // Rotate images every 5 seconds
  useEffect(() => {
    if (!imagesLoaded) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages.length, imagesLoaded]);

  return (
    <HeroSection>
      <HeroImageContainer>
        {imagesLoaded && (
          <>
            {heroImages.map((src, index) => (
              <HeroImage
                key={src}
                src={src}
                alt={`Hero ${index + 1}`}
                $isActive={index === currentImageIndex}
                initial={false}
                animate={{
                  opacity: index === currentImageIndex ? 1 : 0,
                }}
                transition={{
                  duration: 1,
                  ease: [0.4, 0, 0.2, 1],
                }}
              />
            ))}
          </>
        )}
      </HeroImageContainer>
      <Overlay />
      <Container>
        {/* Top Row: Title and Image */}
        <TopRow>
          <TitleColumn
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <TitleWrapper>
              <Title
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.2,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                <div>Swan Pipe Support</div>
                <div>Private Limited</div>
              </Title>
            </TitleWrapper>
          </TitleColumn>
        </TopRow>

        {/* Bottom Row: Dates/Stats and Button */}
        <BottomRow>
          <LeftInfoColumn
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.6,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <DateText>One stop solution for all your</DateText>
            <SquareMeters>Pipe Support needs</SquareMeters>
          </LeftInfoColumn>

          <RightInfoColumn
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.8,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <BuyButton
              as={Link}
              to="/contact"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Enquire Now
            </BuyButton>
            <DownloadButton
              as="button"
              onClick={() => setShowDownloadForm(true)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Catalogue
            </DownloadButton>
          </RightInfoColumn>
        </BottomRow>
      </Container>

      <CatalogueForm
        isOpen={showDownloadForm}
        onClose={() => setShowDownloadForm(false)}
      />
    </HeroSection>
  );
};

export default Hero;
